import { useState } from 'react'
import {
  Container,
  Paper,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
} from '@mui/material'
import { EmojiEvents, Download, Verified } from '@mui/icons-material'
import {
  useGetMyAchievementsQuery,
  useLazyDownloadCertificateQuery,
  type AchievementResult,
} from '../store/api/achievementsApi'
import { PageHeader } from '../components/PageHeader'
import { getContentPaperSx, getTablePaperSx } from '../theme/sectionSurfaces'
import { useAppSelector } from '../hooks/redux'

/** UAT-TC Chung kết #20: Đội thắng xem thành tích & tải chứng chỉ điện tử. */
export default function AchievementsPage() {
  const { data, isLoading, error } = useGetMyAchievementsQuery()
  // YC7 (audit 2026-05-23): sinh viên CHỈ xem hạng, KHÔNG xem điểm cụ thể.
  // Ẩn 2 cột "Điểm TB" + "Tổng điểm" với role student.
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const hideScoreColumns = currentUser?.role === 'student' 
  const [downloadCert] = useLazyDownloadCertificateQuery()
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'warning' | 'info'
  }>({ open: false, message: '', severity: 'info' })

  const handleDownload = async (caseId: string, caseTitle: string) => {
    try {
      const blob = await downloadCert(caseId).unwrap()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Certificate_${caseTitle.replace(/\s+/g, '_')}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      setSnackbar({
        open: true,
        message: 'Đã tải chứng chỉ thành công.',
        severity: 'success',
      })
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: `Tải chứng chỉ thất bại: ${err?.data?.detail || err?.message || ''}`,
        severity: 'error',
      })
    }
  }

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3, display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Container>
    )
  }

  if (error || !data) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <PageHeader title="Thành tích" icon={<EmojiEvents />} />
        <Alert severity="info">
          Bạn chưa thuộc đội thi nào hoặc chưa có kết quả công bố.
        </Alert>
      </Container>
    )
  }

  const summary = data.summary || []
  const bestRank = summary.reduce((min, r) => (r.rank && r.rank < min ? r.rank : min), 999)

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 } }}>
      <PageHeader
        title="Thành tích"
        subtitle={`${data.team_name} — ${data.university}`}
        icon={<EmojiEvents />}
      />

      {/* Hero card: hạng cao nhất */}
      {bestRank < 999 && (
        <Paper
          elevation={0}
          sx={(t) => ({
            ...getContentPaperSx(t),
            mb: 3,
            background: 'linear-gradient(135deg, #FFC107 0%, #FF6F00 100%)',
            color: 'white',
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Verified sx={{ fontSize: 56 }} />
            <Box>
              <Typography variant="overline" sx={{ opacity: 0.9 }}>
                Thành tích cao nhất
              </Typography>
              <Typography variant="h3" fontWeight={700}>
                Hạng {bestRank}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Đã tham gia {summary.length} cuộc thi
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Bảng thành tích từng cuộc thi */}
      <Paper elevation={0} sx={(t) => getContentPaperSx(t)}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Lịch sử thi đấu
        </Typography>
        {summary.length === 0 ? (
          <Alert severity="info">Chưa có kết quả công bố nào.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={(t) => getTablePaperSx(t)}>
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 240 }}><strong>Cuộc thi</strong></TableCell>
                  <TableCell sx={{ minWidth: 80 }}><strong>Hạng</strong></TableCell>
                  {!hideScoreColumns && (
                    <TableCell sx={{ minWidth: 110 }}><strong>Điểm TB</strong></TableCell>
                  )}
                  {!hideScoreColumns && (
                    <TableCell sx={{ minWidth: 110 }}><strong>Tổng điểm</strong></TableCell>
                  )}
                  <TableCell sx={{ minWidth: 130 }}><strong>Ngày công bố</strong></TableCell>
                  <TableCell align="right" sx={{ minWidth: 160 }}><strong>Chứng chỉ</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.map((r: AchievementResult) => (
                  <TableRow key={r.case_id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {r.case_title}
                        {r.advanced && (
                          <Chip label="Vào vòng tiếp" size="small" color="success" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`#${r.rank}`}
                        size="small"
                        color={r.rank === 1 ? 'warning' : r.rank <= 3 ? 'primary' : 'default'}
                      />
                    </TableCell>
                    {!hideScoreColumns && (
                      <TableCell>{r.average_score?.toFixed(1) || '—'}</TableCell>
                    )}
                    {!hideScoreColumns && (
                      <TableCell>{r.total_score?.toFixed(1) || '—'}</TableCell>
                    )}
                    <TableCell>
                      {r.published_at ? new Date(r.published_at).toLocaleDateString('vi-VN') : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={() => handleDownload(r.case_id, r.case_title)}
                        sx={{ textTransform: 'none' }}
                      >
                        Tải PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
